import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client/dist/sockjs';
import { LYFT_SOCKET } from '@src/utils/constants';
import { twilioApi } from '@store/rtk';
import store from '@store/store';
import { logger } from '@utils/logger';
import { incrementAndCleanFromDateString } from '@utils/storage';
import { convertDateExact } from '@src/utils';
import { DRIVER_CHAT_TOPIC_PREFIX } from '@component/DriverChat/constants';
import { driverChatApi } from '@store/rtk/driverChat';
import { toastError } from '@utils/helpers';

const NOTIFICATION_TRIGGER_STATUS_IDS = [3, 4, 20];
const SOCKET_REFRESH_SIZE = 5;

const TICKET_ATTEMPTS = 2;
const TICKET_SKEW_MS = 5000;

/** `expiresAt` is an epoch timestamp; accept seconds or milliseconds. */
const isTicketExpired = (expiresAt: number) => {
  if (!expiresAt) return false;
  const ms = expiresAt < 1e12 ? expiresAt * 1000 : expiresAt;
  return ms - TICKET_SKEW_MS <= Date.now();
};

const isChatTopic = (topic: string) => topic.startsWith(DRIVER_CHAT_TOPIC_PREFIX);

class LyftSocketService {
  private static instance: LyftSocketService | null = null;
  private client: Client | null = null;
  private started = false;
  private isSubscribed = false;
  private activeSubscriptions: StompSubscription[] = [];
  private listeners = new Set<(msg: any, channel: string) => void>();
  private connectionListeners = new Set<(connected: boolean) => void>();
  private connected = false;
  private connectedWithChatTicket = false;
  // Topics added at runtime (payor / open conversation); re-subscribed on every reconnect.
  private dynamicTopics = new Map<string, number>();
  private dynamicSubscriptions = new Map<string, StompSubscription>();

  private channels = [
    '/topic/rides',
    '/topic/flex',
    '/topic/call-logs',
    '/topic/users',
    '/topic/lyft-ride-status',
    '/topic/rides-refresh',
    '/topic/unlock-ride',
  ];

  static getInstance(): LyftSocketService {
    if (!LyftSocketService.instance)
      LyftSocketService.instance = new LyftSocketService();
    return LyftSocketService.instance;
  }

  start() {
    if (this.started) return;
    this.started = true;
    this.isSubscribed = false;
    this.activeSubscriptions = [];

    this.client = new Client({
      webSocketFactory: () =>
        new SockJS(`${LYFT_SOCKET}/ws`) as unknown as WebSocket,
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      // Tickets are single use and expire after 60 s, so every (re)connect fetches a new one.
      beforeConnect: async (client) => {
        const ticket = await this.fetchChatTicket();
        this.connectedWithChatTicket = !!ticket;
        client.connectHeaders = ticket ? { 'X-Chat-Ticket': ticket } : {};
      },
      onConnect: () => {
        logger.log('✅ Connected');
        this.isSubscribed = false;
        this.ensureSubscriptions();
        this.setConnected(true);
      },
      onDisconnect: () => {
        logger.log('🔴 Disconnected');
        this.isSubscribed = false;
        this.activeSubscriptions = [];
        this.dynamicSubscriptions.clear();
        this.setConnected(false);
      },
      onWebSocketError: (err) => {
        logger.log('Websocket error', err);
      },
      onWebSocketClose: (event) => {
        logger.log('Websocket closed', event);
        this.setConnected(false);
      },
    });
    this.client.activate();
  }

  stop() {
    if (!this.started) return;
    this.started = false;
    this.isSubscribed = false;
    this.activeSubscriptions = [];
    this.dynamicSubscriptions.clear();
    this.client?.deactivate();
    this.client = null;
    this.setConnected(false);
  }

  onMessage(cb: (parsed: any, channel: string) => void) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  isConnected() {
    return this.connected;
  }

  onConnectionChange(cb: (connected: boolean) => void) {
    this.connectionListeners.add(cb);
    return () => this.connectionListeners.delete(cb);
  }

  /**
   * Subscribes to a topic for as long as the returned function is not called.
   * Counted, so two components can share one topic; survives reconnects.
   */
  subscribe(topic: string) {
    this.dynamicTopics.set(topic, (this.dynamicTopics.get(topic) ?? 0) + 1);
    this.subscribeDynamic(topic);
    return () => {
      const count = (this.dynamicTopics.get(topic) ?? 1) - 1;
      if (count > 0) {
        this.dynamicTopics.set(topic, count);
        return;
      }
      this.dynamicTopics.delete(topic);
      this.dynamicSubscriptions.get(topic)?.unsubscribe();
      this.dynamicSubscriptions.delete(topic);
    };
  }

  /**
   * The ticket needs the login identity headers, which can land just after the
   * socket first connects. If this connection went out without a ticket, cycle
   * it once so the chat topics get a ticketed session.
   */
  async ensureChatTicket() {
    if (!this.started || !this.client) return;
    if (this.connectedWithChatTicket) return;
    const client = this.client;
    await client.deactivate();
    if (this.started && this.client === client) client.activate();
  }

  /**
   * GET /api/twilio/driver-chat/ws-ticket -> { ticket, expiresAt }. The ticket is
   * single use and short lived, so it is never cached: every (re)connect asks for
   * a new one, and one that arrives already expired (slow response, clock skew)
   * is replaced once before connecting.
   */
  private async fetchChatTicket(): Promise<string | null> {
    try {
      let ticket: string | null = null;
      for (let attempt = 0; attempt < TICKET_ATTEMPTS; attempt += 1) {
        const result = await store
          .dispatch(
            driverChatApi.endpoints.getDriverChatWsTicket.initiate(undefined, {
              forceRefetch: true,
            })
          )
          .unwrap();
        ticket = result.ticket;
        if (!isTicketExpired(result.expiresAt)) break;
      }
      return ticket;
    } catch {
      // Not a chat user (or identity headers not ready): ride channels still connect.
      logger.log('Chat ticket unavailable');
      return null;
    }
  }

  private subscribeDynamic(topic: string) {
    if (!this.client?.connected || this.dynamicSubscriptions.has(topic)) return;
    this.dynamicSubscriptions.set(
      topic,
      this.client.subscribe(topic, (msg: IMessage) => this.dispatchMessage(topic, msg))
    );
  }

  private dispatchMessage(ch: string, msg: IMessage) {
    try {
      const parsed = JSON.parse(msg.body);

      // Chat payloads can contain PHI: never log them or run ride side effects.
      if (isChatTopic(ch)) {
        this.listeners.forEach((fn) => fn(parsed, ch));
        return;
      }

      logger.log(`📨 Message from ${ch}:`, parsed);

      if (parsed?.dateOfService) {
        incrementAndCleanFromDateString(
          convertDateExact(parsed?.dateOfService),
          parsed?.rideId
        );
      }

      this.handleNotificationTrigger(parsed);

      this.listeners.forEach((fn) => fn(parsed, ch));
    } catch (error) {
      toastError(error);
      logger.error('Socket Parse Error', ch);
    }
  }

  private setConnected(connected: boolean) {
    if (this.connected === connected) return;
    this.connected = connected;
    this.connectionListeners.forEach((fn) => fn(connected));
  }

  private handleNotificationTrigger(parsed: any) {
    const isTrackPage = window.location.pathname.includes('/track');
    const isRelevantStatus = NOTIFICATION_TRIGGER_STATUS_IDS.includes(
      parsed.statusId
    );

    if (!isRelevantStatus || isTrackPage) return;

    const { isActiveFilter } = store.getState().notifications;

    store.dispatch(
      twilioApi.endpoints.getNotification.initiate(
        {
          page: 0,
          size: SOCKET_REFRESH_SIZE,
          ...(isActiveFilter ? { isRead: false } : {}),
        },
        { forceRefetch: true }
      )
    );
  }

  private ensureSubscriptions() {
    if (!this.client?.connected || this.isSubscribed) return;
    this.isSubscribed = true;

    this.activeSubscriptions.forEach((s) => s.unsubscribe());
    this.activeSubscriptions = [];

    this.channels.forEach((ch) => {
      const sub = this.client!.subscribe(ch, (msg: IMessage) =>
        this.dispatchMessage(ch, msg)
      );
      this.activeSubscriptions.push(sub);
    });

    this.dynamicSubscriptions.clear();
    this.dynamicTopics.forEach((_count, topic) => this.subscribeDynamic(topic));
  }
}

export default LyftSocketService.getInstance();
